import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
const privacyContent = {
  title: 'Política de Privacidad',
  sections: [{
    heading: '1. Información que Recopilamos',
    text: 'Recopilamos información personal que nos proporcionas directamente al realizar una compra, como tu nombre, dirección de correo electrónico y datos de pago. También recopilamos información automáticamente cuando visitas nuestro sitio, incluyendo tu dirección IP, tipo de navegador y páginas visitadas.'
  }, {
    heading: '2. Uso de la Información',
    text: 'Utilizamos tu información para procesar transacciones, enviar el producto digital adquirido, comunicarnos contigo sobre tu compra, mejorar nuestros productos y servicios, y enviarte información relevante sobre actualizaciones del producto (puedes darte de baja en cualquier momento).'
  }, {
    heading: '3. Protección de Datos',
    text: 'Implementamos medidas de seguridad técnicas y organizativas para proteger tu información personal contra acceso no autorizado, alteración, divulgación o destrucción. Tus datos de pago son procesados de forma segura a través de proveedores de pago certificados.'
  }, {
    heading: '4. Compartir Información',
    text: 'No vendemos, comercializamos ni transferimos tu información personal a terceros. Esto no incluye terceros de confianza que nos ayudan a operar nuestro sitio web o a realizar nuestro negocio, siempre que dichas partes acuerden mantener esta información confidencial.'
  }, {
    heading: '5. Tus Derechos',
    text: 'Tienes derecho a acceder, rectificar, eliminar y portar tus datos personales. También puedes oponerte al procesamiento de tus datos o solicitar la limitación del mismo. Para ejercer estos derechos, contáctanos por correo electrónico.'
  }, {
    heading: '6. Cookies',
    text: 'Nuestro sitio utiliza cookies para mejorar tu experiencia de navegación, analizar el tráfico del sitio y personalizar contenido. Puedes configurar tu navegador para rechazar cookies, aunque esto podría afectar la funcionalidad del sitio.'
  }]
};
const termsContent = {
  title: 'Términos y Condiciones',
  sections: [{
    heading: '1. Aceptación de los Términos',
    text: 'Al acceder y realizar una compra en este sitio web, aceptas cumplir con estos términos y condiciones de uso. Si no estás de acuerdo con alguna parte de estos términos, no debes utilizar nuestro sitio ni adquirir nuestros productos.'
  }, {
    heading: '2. Producto Digital',
    text: 'Creator OS v2026 es un producto digital (ebook) que se entrega electrónicamente. Al completar tu compra, recibirás acceso inmediato al material a través del correo electrónico proporcionado. El contenido es para uso personal y no puede ser redistribuido, revendido ni compartido.'
  }, {
    heading: '3. Política de Reembolso',
    text: 'Ofrecemos una garantía de satisfacción de 7 días. Si no estás satisfecho con el producto, puedes solicitar un reembolso completo dentro de los 7 días posteriores a la compra enviando un correo electrónico. El reembolso se procesará en un plazo de 5-10 días hábiles.'
  }, {
    heading: '4. Propiedad Intelectual',
    text: 'Todo el contenido incluido en Creator OS v2026, incluyendo textos, gráficos, templates, prompts y metodologías, está protegido por derechos de autor. Se otorga una licencia personal e intransferible para uso del comprador únicamente.'
  }, {
    heading: '5. Resultados',
    text: 'Los testimonios y resultados mostrados en esta página son experiencias individuales y no garantizan resultados específicos. Los resultados varían según el esfuerzo, nicho, audiencia y otros factores individuales de cada persona.'
  }, {
    heading: '6. Limitación de Responsabilidad',
    text: 'No nos hacemos responsables de daños directos, indirectos, incidentales o consecuentes que resulten del uso o la imposibilidad de uso de nuestros productos. Nuestro producto es educativo e informativo y no constituye asesoría financiera, legal ni profesional.'
  }, {
    heading: '7. Modificaciones',
    text: 'Nos reservamos el derecho de modificar estos términos en cualquier momento. Las modificaciones entrarán en vigor inmediatamente después de su publicación en el sitio web. El uso continuado del sitio después de dichos cambios constituye tu aceptación de los nuevos términos.'
  }]
};
function Modal({
  isOpen,
  onClose,
  content
}: {
  isOpen: boolean;
  onClose: () => void;
  content: typeof privacyContent;
}) {
  return <AnimatePresence data-id="element-32">
      {isOpen && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[60]" onClick={onClose} data-id="element-33" />
          <motion.div initial={{
        opacity: 0,
        y: 40,
        scale: 0.95
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 40,
        scale: 0.95
      }} transition={{
        duration: 0.3,
        ease: 'easeOut'
      }} className="fixed inset-4 md:inset-x-auto md:inset-y-8 md:max-w-2xl md:mx-auto bg-[#111111] border border-white/10 rounded-2xl z-[61] overflow-hidden flex flex-col" data-id="element-34">
            <div className="flex items-center justify-between p-6 border-b border-white/10 flex-shrink-0" data-id="element-35">
              <h2 className="text-xl font-bold text-white" data-id="element-36">{content.title}</h2>
              <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10 transition-colors" data-id="element-37">
                <XIcon className="w-5 h-5 text-gray-400" data-id="element-38" />
              </button>
            </div>

            <div className="overflow-y-auto flex-1 p-6 space-y-6" data-id="element-39">
              <p className="text-sm text-gray-500" data-id="element-40">
                Última actualización: Marzo 2026
              </p>
              {content.sections.map((section, i) => <div key={i} data-id="element-41">
                  <h3 className="text-white font-semibold mb-2" data-id="element-42">
                    {section.heading}
                  </h3>
                  <p className="text-gray-400 leading-relaxed text-sm" data-id="element-43">
                    {section.text}
                  </p>
                </div>)}
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="bg-[#0a0a0a] border-t border-white/5 py-8 px-4" data-id="element-44">
        <div className="max-w-4xl mx-auto flex flex-col items-center gap-4" data-id="element-45">
          <div className="flex items-center gap-6" data-id="element-46">
            <button onClick={() => setActiveModal('privacy')} className="text-sm text-gray-500 hover:text-indigo-400 transition-colors" data-id="element-47">
              Política de Privacidad
            </button>
            <span className="text-gray-700" data-id="element-48">•</span>
            <button onClick={() => setActiveModal('terms')} className="text-sm text-gray-500 hover:text-indigo-400 transition-colors" data-id="element-49">
              Términos y Condiciones
            </button>
          </div>
          <p className="text-xs text-gray-600" data-id="element-50">
            © {new Date().getFullYear()} Creator OS. Todos los derechos
            reservados.
          </p>
        </div>
      </footer>

      <Modal isOpen={activeModal === 'privacy'} onClose={() => setActiveModal(null)} content={privacyContent} data-id="element-51" />
      <Modal isOpen={activeModal === 'terms'} onClose={() => setActiveModal(null)} content={termsContent} data-id="element-52" />
    </>;
}